import { useTranslation } from "react-i18next";
import { AlertOutlined, CloudUploadOutlined, EditOutlined } from "@ant-design/icons";
import { DATE_OPTIONS } from "../../../core/utils/date-utils";
import {
    QuickChip,
    QuickDateRange,
    QuickModalityFilter,
    QuickResetLink,
    QuickSearch,
    QuickSegment,
    useAppliedFilter,
    useQuickSearch,
} from "../../quick-filter/QuickFilterParts";
import { ExtraFiltersButton, ExtraFilterTags, isFieldActive } from "../../quick-filter/ExtraFilters";
import { buildDefaultReportFilter } from "./report-filter-defaults";
import { REPORT_EXTRA_FIELDS } from "./report-extra-filters";

// Son 15 gün sayfanın varsayılanı (bkz. report-filter-defaults).
const DATE_PRESETS = [
    { key: 'today', label: 'Today', option: DATE_OPTIONS.TODAY },
    { key: 'yesterday', label: 'Yesterday', option: DATE_OPTIONS.YESTERDAY },
    { key: 'last15', label: 'Last 15D', option: DATE_OPTIONS.LAST_15_DAYS },
    { key: 'last30', label: 'Last 30D', option: DATE_OPTIONS.LAST_30_DAYS },
];

// Sol paneldeki tarih türleri; öncelik panelin geri yüklemesiyle aynı: imza > çekim > rapor.
const DATE_TYPES = {
    sign: { beginKey: 'signDateBegin', endKey: 'signDateEnd', label: 'Signature' },
    study: { beginKey: 'studyDateBegin', endKey: 'studyDateEnd', label: 'Study Date' },
    report: { beginKey: 'reportDateBegin', endKey: 'reportDateEnd', label: 'Report Date' },
};

/**
 * Rapor grid'inin üstündeki hızlı filtre çubuğu — çekim grid'indekiyle aynı düzen ve parçalar (quick-filter/):
 * arama, tarih, onay durumu, durum çipleri, modalite, diğer filtreler. Sol panelde (ReportFilter) olup
 * çubukta düğmesi olmayan her şey "Diğer filtreler"de; uzun vadede sol panelin yerini alır.
 */
export default function ReportQuickFilterBar() {
    const { t } = useTranslation();
    const { filter, apply, replace, ready } = useAppliedFilter('reportFilter');
    const search = useQuickSearch(filter, apply);

    if (!ready)
        return null;

    const dateType = (filter.signDateBegin || filter.signDateEnd)
        ? DATE_TYPES.sign
        : (filter.studyDateBegin || filter.studyDateEnd) ? DATE_TYPES.study : DATE_TYPES.report;

    const emergency = filter.isEmergency === true;
    const notSigned = filter.isSign === false;
    const notTransferred = filter.isHbysTransfer === false;

    const extraCount = REPORT_EXTRA_FIELDS.filter(field => isFieldActive(field, filter)).length;

    return (
        <div className="qf">
            <QuickSearch
                search={search}
                hint={t('Search by patient name, ID, accession number or description - searches all dates')}
            />

            <div className="qf-row">
                <QuickDateRange
                    filter={filter}
                    apply={apply}
                    beginKey={dateType.beginKey}
                    endKey={dateType.endKey}
                    presets={DATE_PRESETS}
                    disabled={search.active}
                    label={t(dateType.label)}
                />

                <div className="qf-group">
                    <QuickSegment
                        ariaLabel={t('Confirming Status')}
                        value={filter.isConfirm ?? null}
                        onChange={(value) => apply({ isConfirm: value })}
                        options={[
                            { value: null, label: t('All') },
                            { value: true, label: t('Confirmed') },
                            { value: false, label: t('Not Confirmed') },
                        ]}
                    />
                </div>

                <div className="qf-group">
                    <QuickChip
                        pressed={emergency}
                        onClick={() => apply({ isEmergency: emergency ? null : true })}
                        icon={<AlertOutlined className="qf-icon-emergency" aria-hidden="true" />}
                    >
                        {t('Emergency')}
                    </QuickChip>
                    <QuickChip
                        pressed={notSigned}
                        onClick={() => apply({ isSign: notSigned ? null : false })}
                        icon={<EditOutlined aria-hidden="true" />}
                    >
                        {t('Not Signed')}
                    </QuickChip>
                    <QuickChip
                        pressed={notTransferred}
                        onClick={() => apply({ isHbysTransfer: notTransferred ? null : false })}
                        icon={<CloudUploadOutlined aria-hidden="true" />}
                    >
                        {t('Not sent to HBYS')}
                    </QuickChip>
                </div>

                <QuickModalityFilter filter={filter} apply={apply} />

                <div className="qf-group">
                    <ExtraFiltersButton
                        fields={REPORT_EXTRA_FIELDS}
                        filter={filter}
                        apply={apply}
                        activeCount={extraCount}
                    />
                </div>

                <QuickResetLink onClick={() => replace(buildDefaultReportFilter())} />
            </div>

            <ExtraFilterTags fields={REPORT_EXTRA_FIELDS} filter={filter} apply={apply} />
        </div>
    );
}
